import { html } from "lit";
import { MenuItem } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeModel, NodeSource, TreeSource } from "@omegagrid/tree";
import rp, { BrowserAdapter, BrowserOptions, formatDateTime } from "@reporting/rp-app-host";
import { ETLJob, JobStep } from "@reporting/rp-types/jobs";
import { AccountingRights } from "@reporting/rp-types/core";
import { EntityAccessType } from "@reporting/rp-types/users";

export class JobBrowserAdapter extends BrowserAdapter {

	getTooltip(node: NodeModel) {
		const job = node.properties?.job as ETLJob;
		if (!job || node.level > 0) return null;
		return html`<div>
			${msg('Description')}: ${job.description}<br>
			${msg('Order')}: ${job.ord_num ?? '-'}<br>
			${msg('Last run')}: ${job.last_run ? formatDateTime(job.last_run) : msg('Never')}<br>
			${msg('Next run')}: ${job.next_run ? formatDateTime(job.next_run) : msg('Never')}<br>
		</div>`;
	}

	async getTree(): Promise<TreeSource> {
		const treeItems: NodeSource[] = [];
		const jobs = await rp.ms('jobs').get<ETLJob[]>('jobsWithSteps');
		const jobNames = new Set<string>(); // to avoid duplicates
		for (const job of jobs) {
			if (!jobNames.has(job.job_name)) {
				jobNames.add(job.job_name);
				treeItems.push({
					k: job.job_name,
					v: job.job_name,
					l: 0,
					collapsed: true,
					icon: 'gears',
					props: { job: job, menuItems: this._createJobMenuItems() },
				});
			}
			if (job.steps) {
				const steps = Object.values(job.steps);
				for (const step of steps) {
					treeItems.push({
						k: `jobs-stepSqlEditor-${btoa(job.job_name)}-${step.step_id}`,
						v: step.step_name,
						l: 1,
						icon: 'square-terminal',
						props: { job: job, jobStep: step, menuItems: this._createStepMenuItems(step.step_id, steps.length) },
					});
				}
			}
		}
		return treeItems;
	}

	_createJobMenuItems(): MenuItem[] {
		const items: MenuItem[] = [
			{ key: 'plan', value: msg('Plan'), icon: 'calendar-days' },
			{ key: 'history', value: msg('History'), icon: 'clock-rotate-left' },
		];
		if (rp.userProvider.hasRight(AccountingRights.JobsDefine)) {
			items.push(
				{ key: 'properties', value: msg('Properties'), icon: 'pen-to-square' },
				{ key: 'params', value: msg('Parameters'), icon: 'sliders' },
				{ key: 'addStep', value: msg('Add Step'), icon: 'plus' },
				{ key: 'access', value: msg('Access'), icon: 'shield-check' },
				{ key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000' }
			);
		}
		return items;
	}

	_createStepMenuItems(id: number, stepsCount: number): MenuItem[] {
		if (!rp.userProvider.hasRight(AccountingRights.JobsDefine)) return [];
		const items: MenuItem[] = [
			{ key: 'stepProperties', value: msg('Properties'), icon: 'pen-to-square' },
			{ key: 'stepDelete', value: msg('Delete'), icon: 'trash', color: '#ff0000' }
		];
		if (id > 1) items.push({ key: 'stepMoveUp', value: msg('Move Up'), icon: 'arrow-up' });
		if (id < stepsCount) items.push({ key: 'stepMoveDown', value: msg('Move Down'), icon: 'arrow-down' });
		return items;
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		if (node.properties) {
			return node.properties.menuItems as MenuItem[] || [];
		}
		return [];
	}

	async switchSteps(jobName: string, stepId: number, direction: 'up' | 'down'): Promise<void> {
		await rp.ms('jobs').post('switchSteps', {
			job_name: jobName,
			step_id_1: direction === 'up' ? stepId - 1 : stepId,
			step_id_2: direction === 'up' ? stepId : stepId + 1
		});
		direction === 'up' ? this.browser.tree.list.moveUp() : this.browser.tree.list.moveDown();
		this.browser.refresh();
	}

	async onAction(key: string, node: NodeModel) {
		const job = node.properties?.job as ETLJob;
		const jobStep = node.properties?.jobStep as JobStep;
		switch (key) {
			// job actions
			case 'properties':
				rp.host.openModalSidebar({ id: `jobs-jobForm-${btoa(job.job_name)}`, caption: msg('Edit job') });
				break;
			case 'plan':
				await rp.host.open(`jobs-plan-${btoa(job.job_name)}`, { title: `${msg('Plan')}: ${job.job_name}` });
				break;
			case 'history':
				await rp.host.open(`jobs-history-${btoa(job.job_name)}`, { title: `${msg('History')}: ${job.job_name}` });
				break;
			case 'params':
				await rp.host.open(`jobs-params-${btoa(job.job_name)}`, { title: `${msg('Parameters')}: ${job.job_name}` });
				break;
			case 'addStep':
				rp.host.openModalSidebar({ id: `jobs-stepForm-${btoa(job.job_name)}`, caption: msg('Add step') });
				break;
			case 'access':
				rp.host.open(`users-entityUsers-${EntityAccessType.Job}-${job.job_name}`, { title: `${msg('Access')}: ${job.job_name}` });
				break;
			case 'delete':
				if ((await rp.host.openMenu({
					text: `${msg('Are you sure you want to delete')} '${job.job_name}'?`,
					content: 'yes_no'
				})).id == 'Y') {
					await rp.ms('jobs').delete('jobs', { job_name: job.job_name });
				}
				this.browser.refresh();
				break;
			// step actions
			case 'stepProperties':
				rp.host.openModalSidebar({ id: `jobs-stepForm-${btoa(job.job_name)}-${jobStep.step_id}`, caption: msg('Edit step') });
				break;
			case 'stepMoveUp':
				await this.switchSteps(job.job_name, jobStep.step_id, 'up');
				break;
			case 'stepMoveDown':
				await this.switchSteps(job.job_name, jobStep.step_id, 'down');
				break;
			case 'stepDelete':
				if ((await rp.host.openMenu({
					text: `${msg('Are you sure you want to delete')} '${jobStep.step_name}'?`,
					content: 'yes_no'
				})).id == 'Y') {
					await rp.ms('jobs').delete(`steps/${jobStep.step_id}`, { job_name: job.job_name });
				}
				this.browser.refresh();
				break;
		}
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			search: true,
			contextMenuButton: true,
			afterRenderer: (div, node) => {
				const job = node.properties?.job as ETLJob;
				div.style.borderLeft = node.level === 0 && job
					? `3px solid ${job.enabled ? '#00ff00' : '#ff0000'}`
					: 'none'
			},
		});
	}

}